import { DataGridFilters } from '@/components/data-grid-filters/DataGridFilters.component';
import { useUserToken } from '@/contexts/UserTokenContext';
import { AmendmentsBilling } from '@/models/Amendments.model';
import { Search } from '@mui/icons-material';
import CachedIcon from '@mui/icons-material/Cached';
import {
  Autocomplete,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField
} from '@mui/material';

import { Field, Form, Formik } from 'formik';
import { capitalize, isEqual } from 'lodash';
import React, { useCallback, useState } from 'react';

import { defaultFilters } from '../AmendmentsPage.route';

export type AmendmentsFilters = {
  adminStatus: string;
  billableAmount: string[];
  firmId: string;
  productTierId: number[];
  search: string;
  searchType: string;
  assignee: string;
};

type AmendmentsFiltersProps = {
  setUpdatedFilters: (filters: AmendmentsFilters) => void;
};

export const initialAmendmentFilters: AmendmentsFilters = {
  adminStatus: '',
  assignee: '',
  billableAmount: [],
  firmId: '',
  productTierId: [],
  search: '',
  searchType: ''
};

enum PRODUCT_TIER_ID {
  'Workplace' = 1,
  'Plus' = 2,
  'Pro' = 3,
  'Vestwell Flex' = 4,
  'Recordkeeping Only' = 5
}

export const AmendmentsFilters: React.FC<AmendmentsFiltersProps> = props => {
  const [, setFilters] = useState(initialAmendmentFilters);
  const { token } = useUserToken();

  const handleSubmit = useCallback(
    data => {
      const getProductTierIds = (values: string[]) =>
        values.map(tier => +PRODUCT_TIER_ID[tier]);

      const getBillableTypes = (values: string[]) => {
        const billableTypes = [
          AmendmentsBilling.DISCOUNTED_FEE,
          AmendmentsBilling.GENERAL_AMENDMENT_FEE,
          AmendmentsBilling.JPM_WILL_COVER_FEE,
          AmendmentsBilling.COMPEX_AMENDMENT_FEE,
          AmendmentsBilling.INTERNAL_MERGER_FEE,
          AmendmentsBilling.EXTERNAL_MERGER_FEE
        ];

        const nonBillableTypes = [
          AmendmentsBilling.WAIVED___FEE_INCLUDED_IN_CONVERSION,
          AmendmentsBilling.WAIVED___ADDRESS_CHANGE,
          AmendmentsBilling.WAIVED___CLIENT_OR_FA_RELATIONSHIP,
          AmendmentsBilling.WAIVED___OTHER__ADD_TO_DESCRIPTION,
          AmendmentsBilling.WAIVED___TPA_SUBMISSION,
          AmendmentsBilling.WAIVED___LOAN_POLICY_CHANGE,
          AmendmentsBilling.WAIVED___VW_ERROR
        ];

        if (values.length > 1) return [];
        if (values.includes('Yes - Billable')) return billableTypes;
        if (values.includes('No - Not Billable')) return nonBillableTypes;
      };

      setFilters(data);
      props.setUpdatedFilters({
        ...data,
        billableAmount: getBillableTypes(data.billableAmount),
        productTierId: getProductTierIds(data.productTierId)
      });
    },
    [props.setUpdatedFilters]
  );

  return (
    <DataGridFilters removeBorderRight>
      <Formik initialValues={initialAmendmentFilters} onSubmit={handleSubmit}>
        {formProps => {
          const areFiltersEmpty = isEqual(
            formProps.values,
            initialAmendmentFilters
          );

          return (
            <Form data-testid='amendments-filters'>
              <Stack gap={2} width={250}>
                <FormControl fullWidth size='small'>
                  <Field
                    InputProps={{
                      'aria-placeholder': 'Search',
                      endAdornment: (
                        <InputAdornment position='end'>
                          <Search />
                        </InputAdornment>
                      ),
                      sx: { '& .MuiOutlinedInput-input': { pb: 1, pt: 1 } }
                    }}
                    as={TextField}
                    data-testid='amendments-filters-search'
                    id='plan-search'
                    label='Search plan by name or ID'
                    name='search'
                    size='small'
                    variant='outlined'
                  />
                </FormControl>

                <FormControl fullWidth size='small' variant='outlined'>
                  <InputLabel id='plan-type-label'>Plan Type</InputLabel>
                  <Select
                    data-testid='amendments-filters-plan-type-select'
                    label='Plan Type'
                    labelId='plan-type-label'
                    onChange={e =>
                      formProps.setFieldValue('searchType', e.target.value)
                    }
                    value={formProps.values.searchType}>
                    <MenuItem value='planName'>Plan Name</MenuItem>
                    <MenuItem value='planId'>Plan ID</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <Autocomplete
                    data-testid='amendments-filters-plan-tier'
                    disableCloseOnSelect
                    multiple
                    onChange={(e, values) =>
                      formProps.setFieldValue('productTierId', values)
                    }
                    options={[
                      'Workplace',
                      'Plus',
                      'Pro',
                      'Vestwell Flex',
                      'Recordkeeping Only'
                    ]}
                    renderInput={params => (
                      <TextField {...params} label='Plan Tier' size='small' />
                    )}
                    renderOption={(rootProps, option, { selected }) => (
                      <MenuItem
                        {...rootProps}
                        data-value={option}
                        style={{ whiteSpace: 'normal' }}>
                        <Checkbox checked={selected} sx={{ py: 0 }} />
                        {option}
                      </MenuItem>
                    )}
                    size='small'
                    value={formProps.values.productTierId}
                  />
                </FormControl>

                <FormControl fullWidth>
                  <Autocomplete
                    data-testid='amendments-filters-billable'
                    disableCloseOnSelect
                    multiple
                    onChange={(e, values) =>
                      formProps.setFieldValue('billableAmount', values)
                    }
                    options={['Yes - Billable', 'No - Not Billable']}
                    renderInput={params => (
                      <TextField {...params} label='Billable' size='small' />
                    )}
                    renderOption={(rootProps, option, { selected }) => (
                      <MenuItem
                        {...rootProps}
                        data-value={option}
                        style={{ whiteSpace: 'normal' }}>
                        <Checkbox checked={selected} sx={{ py: 0 }} />
                        {option}
                      </MenuItem>
                    )}
                    size='small'
                    value={formProps.values.billableAmount}
                  />
                </FormControl>

                <Divider component='div' role='presentation' textAlign='left'>
                  Meta
                </Divider>

                <FormGroup>
                  <Grid container paddingLeft={2} rowGap={2}>
                    <Grid>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={Boolean(formProps.values.assignee)}
                            data-testid='amendments-filters-assignee'
                            onChange={e => {
                              const assigneeName = token?.userId
                                .split('.')
                                .map(name => capitalize(name))
                                .join(' ');

                              formProps.setFieldValue(
                                'assignee',
                                e.target.checked ? assigneeName : null
                              );
                            }}
                            size='small'
                          />
                        }
                        label='Assigned to me'
                      />
                    </Grid>
                    <Grid>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formProps.values.adminStatus}
                            data-testid='amendments-filters-admin-status'
                            onChange={e =>
                              formProps.setFieldValue(
                                'adminStatus',
                                e.target.checked ? 'Onboarding' : null
                              )
                            }
                            size='small'
                          />
                        }
                        label='Onboarding Plan'
                      />
                    </Grid>

                    <Grid>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formProps.values.firmId}
                            data-testid='amendments-filters-firm-id'
                            onChange={e =>
                              formProps.setFieldValue(
                                'firmId',
                                e.target.checked ? '1245' : null
                              )
                            }
                            size='small'
                          />
                        }
                        label='JPM Plan'
                      />
                    </Grid>
                  </Grid>
                </FormGroup>

                <Stack
                  flexDirection='row'
                  gap={2}
                  justifyContent='space-between'
                  width='100%'>
                  <Button
                    data-testid='amendments-filters-submit-btn'
                    disabled={!formProps.isValid}
                    onClick={async () => {
                      await formProps.submitForm();
                    }}
                    variant='outlined'>
                    Apply
                  </Button>
                  {!areFiltersEmpty && (
                    <Button
                      data-testid='amendments-filters-clear-btn'
                      onClick={() => {
                        formProps.resetForm();
                        props.setUpdatedFilters(defaultFilters);
                      }}
                      startIcon={<CachedIcon />}
                      variant='text'>
                      Reset
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </DataGridFilters>
  );
};
